import { useApolloClient } from '@apollo/client'
import { useErrorHandlingQuery } from '@backoffice-frontend/data-fetching'
import { useMemo } from 'react'
import {
  OverwatchMapTripAnnotationsDocument,
  type OverwatchMapTripAnnotationsQuery,
  type OverwatchMapTripAnnotationsQueryVariables,
  useOverwatchMapTripAnnotationsQuery,
} from '../Annotations.hook'
import {
  ANNOTATION_PAGE_SIZE,
  appendAnnotationPage,
  MAX_ANNOTATION_PAGES,
} from './annotation-paging'
import type { TripAnnotationMarker } from './types'
import { useRemainingAnnotationPages } from './useRemainingAnnotationPages'

export type UseTripAnnotationsOptions = {
  /**
   * Narrow the layer to one trip / one VTR. Both default to null, which
   * trip-manager reads as "the most recent annotations fleet-wide" — and that is
   * the form worth defaulting to: an annotation marks a place a driver had
   * trouble, which is a property of the road and the approach to it rather than
   * of whichever trip happened to be running, so scoping the layer to the trip
   * on screen hides the notes most worth seeing.
   *
   * It is also the only form that returns anything against real data today:
   * every annotation in production carries neither a trip_id nor a vtr_id,
   * because the uploading client has never populated either field, so a
   * trip- or VTR-scoped query matches nothing at all in any environment. The
   * parameters exist so a caller can scope the layer once that changes.
   */
  tripId?: string | null
  vtrId?: string | null
  /**
   * Scope the fleet-wide form to one metropolis (fleet-manager id). Without it
   * trip-manager returns the newest 500 annotations across every city, so a
   * busy metro crowds out the one the map is showing. Ignored when `tripId` or
   * `vtrId` is set.
   */
  metropolisId?: number | null
  /**
   * Only notes recorded in this window, both ends inclusive epoch ms, either
   * end open. Sent to trip-manager, so a page that shows a few weeks loads
   * those weeks rather than the whole history. Ignored when `tripId` or `vtrId`
   * is set.
   */
  recordedAfterMs?: number | null
  recordedBeforeMs?: number | null
  /** Don't fetch at all — for a caller that mounts the layer behind a toggle or a permission. */
  skip?: boolean
  /**
   * Also fetch each annotation's ADR-0029 classification.
   *
   * Off by default, and gated in the document with `@include(if:)` rather than
   * always selected, because only the triage page reads a classification — the
   * other two callers draw markers and a popup. Leaving it on for them would add
   * an eleven-field object per annotation to a fleet-wide response nothing
   * renders.
   *
   * With this false, `TripAnnotationMarker.classification` is `undefined` rather
   * than `null`: not asked for, as distinct from asked for and absent.
   */
  withClassification?: boolean
  /**
   * Also fetch each annotation's speaker turns and transcript-correction
   * metadata (ADR-0034).
   *
   * Off by default and gated with `@include(if:)` for the same reason as
   * {@link withClassification}: only the triage page's edit dialog reads them.
   * With this false, `TripAnnotationMarker.transcriptTurns` and
   * `transcriptCorrection` are `undefined` rather than empty or `null`.
   */
  withTranscriptEditing?: boolean
  /**
   * Keep fetching older pages after the first until the fleet-wide list is
   * exhausted or {@link MAX_ANNOTATION_PAGES} pages are loaded.
   *
   * Off by default: the map layers want the newest page only, and each extra
   * page is another round trip. The triage tabs turn it on because they
   * summarise the whole history, and a window of the newest 500 is dominated by
   * whichever city recorded most recently, so a chart or table built from it
   * says little about the fleet. Ignored when `tripId` or `vtrId` is set, since
   * trip-manager returns those unpaged.
   *
   * The first page is returned as soon as it lands. The older pages are then
   * loaded several at a time (see `useRemainingAnnotationPages`) and added to
   * the list in one go once they are all in, so the list grows once rather
   * than page by page, and every table built on it renders once.
   */
  fetchAllPages?: boolean
  /**
   * Most pages {@link fetchAllPages} requests, the first included. Defaults to
   * {@link MAX_ANNOTATION_PAGES}. A map passes a lower cap, because it holds
   * every row in memory for as long as it is open.
   */
  maxPages?: number
}

export type UseTripAnnotationsResult = {
  annotations: TripAnnotationMarker[]
  /** The first page is still loading. */
  loading: boolean
  /**
   * Older pages are still loading, so `annotations` is only the newest page.
   * They arrive together, all at once.
   */
  loadingMore: boolean
  /**
   * `annotations` may be missing older rows: pages are still loading, loading
   * stopped on an error or the page cap, or `fetchAllPages` is off and the
   * first page came back full.
   */
  truncated: boolean
}

/**
 * Fetches driver voice notes and maps them to the shape the annotation layer
 * draws ({@link TripAnnotationMarker}).
 *
 * Owned by this lib rather than by either consuming area so both maps fetch the
 * same fields through the same operation — the popup and marker code below read
 * `hasAudio`, `transcriptionStatus` and `vtrId`, and an area that assembled its
 * own query would silently render a degraded popup the moment it forgot one.
 * Rows are passed through unfiltered; {@link useAnnotationsState} validates
 * coordinates and applies the user's filters.
 */
export const useTripAnnotations = ({
  tripId = null,
  vtrId = null,
  metropolisId = null,
  recordedAfterMs = null,
  recordedBeforeMs = null,
  skip = false,
  withClassification = false,
  withTranscriptEditing = false,
  fetchAllPages = false,
  maxPages = MAX_ANNOTATION_PAGES,
}: UseTripAnnotationsOptions = {}): UseTripAnnotationsResult => {
  const client = useApolloClient()
  const isScoped = Boolean(tripId || vtrId)
  // Every variable but the page, so a later page is asked for exactly as the
  // first one was.
  const baseVariables: Omit<
    OverwatchMapTripAnnotationsQueryVariables,
    'limit' | 'offset'
  > = {
    tripId,
    vtrId,
    metropolisId,
    recordedAfterMs,
    recordedBeforeMs,
    withClassification,
    withTranscriptEditing,
  }
  const variables = { ...baseVariables, limit: ANNOTATION_PAGE_SIZE, offset: 0 }
  const { data, loading, updateQuery } = useErrorHandlingQuery(
    useOverwatchMapTripAnnotationsQuery,
    { variables, skip },
  )

  const rows = data?.tripAnnotations
  const { loadingMore, stoppedEarly } = useRemainingAnnotationPages({
    rows,
    loading,
    enabled: fetchAllPages && !isScoped && !skip,
    variablesKey: JSON.stringify(baseVariables),
    scope: client,
    maxPages,
    // `no-cache`: each page is held until the last one is in and then written
    // into this query's own cache entry once, rather than every page becoming
    // a cache entry of its own that nothing reads again.
    fetchPage: offset =>
      client
        .query<
          OverwatchMapTripAnnotationsQuery,
          OverwatchMapTripAnnotationsQueryVariables
        >({
          query: OverwatchMapTripAnnotationsDocument,
          variables: { ...variables, offset },
          fetchPolicy: 'no-cache',
        })
        .then(result => result.data.tripAnnotations),
    // Appended to what the cache holds now, not to the rows the load started
    // from, so a note answered while the pages loaded keeps its new state.
    appendRows: older => {
      let written: readonly { annotationId: string }[] = []
      updateQuery(previous => {
        const tripAnnotations = appendAnnotationPage(
          previous.tripAnnotations,
          older,
        )
        written = tripAnnotations
        return { ...previous, tripAnnotations }
      })
      return written
    },
  })

  const loadedCount = rows?.length ?? 0

  const annotations = useMemo(
    () =>
      data?.tripAnnotations.map(
        (annotation): TripAnnotationMarker => ({
          id: annotation.annotationId,
          recordedAtMs: annotation.recordedAtMs,
          vehicleId: annotation.vehicleId,
          driverId: annotation.driverId,
          severity: annotation.severity,
          location: annotation.location,
          transcript: annotation.transcript,
          transcriptionStatus: annotation.transcriptionStatus,
          // `audioS3Uri` is nullable: null while a voicebot recording is still
          // being built, or when there is none. See `TripAnnotationMarker.hasAudio`.
          hasAudio: Boolean(annotation.audioS3Uri),
          tripId: annotation.tripId,
          vtrId: annotation.vtrId,
          answeredAtMs: annotation.answeredAtMs,
          answeredBy: annotation.answeredBy,
          resolution: annotation.resolution
            ? {
                resolvedAtMs: annotation.resolution.resolvedAtMs,
                resolvedBy: annotation.resolution.resolvedBy,
                evidenceType: annotation.resolution.evidenceType,
                evidenceRef: annotation.resolution.evidenceRef,
                note: annotation.resolution.note,
              }
            : null,
          jiraIssueKey: annotation.jiraIssueKey ?? null,
          // Passed through rather than flattened, because the object is the unit
          // that is nullable: ADR-0029 keeps classification in a child table, so
          // an annotation either has a current labeling event or it has none.
          // Spreading it into sibling fields would turn one honest "not
          // classified" into eleven independently-null ones — which is the exact
          // reasoning the contract itself gives for the shape ("hence one
          // nullable object, not several independently-nullable fields").
          //
          // Note the resolver fails closed: if `category` or `source` doesn't map
          // to a GraphQL enum it returns null for the *whole* object rather than
          // erroring, so an unrecognised category reaches the UI as "not
          // classified" rather than as "unknown category".
          //
          // `undefined` when the caller left `withClassification` off, because
          // the field is then absent from the response entirely. That is a
          // different fact from `null`, and the type keeps them apart.
          classification: annotation.classification,
          // Same three-state contract as `classification`: `undefined` when
          // `withTranscriptEditing` is off, because the fields are then absent
          // from the response.
          transcriptTurns: annotation.transcriptTurns,
          transcriptCorrection: annotation.transcriptCorrection,
        }),
      ) ?? [],
    [data],
  )

  const truncated = fetchAllPages
    ? loadingMore || stoppedEarly
    : loadedCount >= ANNOTATION_PAGE_SIZE

  return { annotations, loading, loadingMore, truncated }
}
